import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';

/** A build the DDR is still writing up: the studio is open before the build exists. */
export type PendingStudio = {
    roomId: string;
    toolUseId: string | null;
    ddrName: string;
    /** Where leaving the studio goes: the chat the build was asked for in. */
    returnTo: string;
    /** An edit's app, opened as soon as the call's arguments named it. */
    openedAppId: string | null;
    /** True once the call's arguments named no app: the build makes a new one. */
    newApp: boolean;
    failed: boolean;
};

const APPS_PATH = '/chat/workspace/apps';

/** Opens the app studio when the DDR starts a start_app_build call, before its arguments finish
 *  streaming, and swaps to the real app when the result names it. Only the tab that sent the message
 *  drives this; another device's frames never navigate. */
@Injectable({ providedIn: 'root' })
export class StudioHandoffService {
    private readonly router = inject(Router);
    private readonly _pending = signal<PendingStudio | null>(null);
    readonly pending = this._pending.asReadonly();

    buildStarting(roomId: string, toolUseId: string | null, ddrName: string): void {
        const current = this._pending();
        if (current && current.roomId === roomId && this.onPendingPage()) {
            this._pending.set({ ...current, toolUseId, ddrName, failed: false });
            return;
        }
        const returnTo = this.router.url;
        if (this.studioAppIn(returnTo) !== null) {
            return;
        }
        this._pending.set({ roomId, toolUseId, ddrName, returnTo, openedAppId: null, newApp: false, failed: false });
        void this.router.navigate([APPS_PATH], { queryParams: { studio: 'pending' }, state: { studioReturnTo: returnTo } });
    }

    /** The call's arguments arrived. An edit names its app, so that studio opens now. */
    buildArgs(toolUseId: string | null, appId: string | null): void {
        const p = this.matching(toolUseId);
        if (p && !appId) {
            this._pending.set({ ...p, newApp: true });
        }
        if (!p || !appId || !this.onPendingPage()) {
            return;
        }
        this._pending.set({ ...p, openedAppId: appId, newApp: false });
        this.openApp(appId, p.returnTo);
    }

    /** Returns false when no pending studio belongs to this call, so the caller opens the studio itself. */
    buildResult(toolUseId: string | null, appId: string | null): boolean {
        const p = this.matching(toolUseId);
        if (!p) {
            return false;
        }
        const here = this.studioAppIn(this.currentUrl());
        const stillWatching = this.onPendingPage() || (p.openedAppId !== null && here === p.openedAppId);
        if (!appId) {
            if (stillWatching) {
                this._pending.set({ ...p, failed: true });
                if (!this.onPendingPage()) {
                    void this.router.navigate([APPS_PATH], {
                        queryParams: { studio: 'pending' },
                        replaceUrl: true,
                        state: { studioReturnTo: p.returnTo },
                    });
                }
            } else {
                this._pending.set(null);
            }
            return true;
        }
        this._pending.set(null);
        if (stillWatching && here !== appId) {
            this.openApp(appId, p.returnTo);
        }
        return true;
    }

    /** The DDR's turn ended. A build that never started says so instead of waiting forever. */
    turnEnded(roomId: string): void {
        const p = this._pending();
        if (p && p.roomId === roomId && !p.failed && this.onPendingPage()) {
            this._pending.set({ ...p, failed: true });
        }
    }

    /** Where leaving goes: the pending build's chat, else the navigation's saved return, else Apps. */
    returnTarget(): string {
        const fromState = (history.state as { studioReturnTo?: unknown } | null)?.studioReturnTo;
        return this._pending()?.returnTo ?? (typeof fromState === 'string' && fromState.startsWith('/') ? fromState : APPS_PATH);
    }

    leave(): void {
        const to = this.returnTarget();
        this._pending.set(null);
        void this.router.navigateByUrl(to);
    }

    private matching(toolUseId: string | null): PendingStudio | null {
        const p = this._pending();
        return p && (p.toolUseId === null || toolUseId === null || p.toolUseId === toolUseId) ? p : null;
    }

    private openApp(appId: string, returnTo: string): void {
        void this.router.navigate([APPS_PATH], { queryParams: { app: appId }, replaceUrl: true, state: { studioReturnTo: returnTo } });
    }

    /** Where the user is, or is being taken: a result can land before the studio's navigation finishes. */
    private currentUrl(): string {
        const nav = this.router.currentNavigation();
        return nav ? this.router.serializeUrl(nav.finalUrl ?? nav.extractedUrl) : this.router.url;
    }

    private onPendingPage(): boolean {
        const url = this.currentUrl();
        return url.startsWith(APPS_PATH) && this.router.parseUrl(url).queryParams['studio'] === 'pending';
    }

    private studioAppIn(url: string): string | null {
        if (!url.startsWith(APPS_PATH)) {
            return null;
        }
        const app = this.router.parseUrl(url).queryParams['app'];
        return typeof app === 'string' && app ? app : null;
    }
}
